import React from 'react';
import { Card, CardContent, Typography, Box, Grid, Stack } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';

// Register required Chart.js elements
ChartJS.register(ArcElement, Tooltip);

// Default configuration for Invoice Overview
const DEFAULT_CONFIG = [
    { id: 'draft', label: 'Draft', color: '#8B5CF6' },
    { id: 'settled', label: 'Settled', color: '#10B981' },
    { id: 'unsettled', label: 'Unsettled', color: '#3B82F6' },
];

export default function OverviewChart({
    title = 'Invoices Overview',
    subtitle = '',
    centerLabel = 'Total',
    config = DEFAULT_CONFIG,
    data = {},
    valueKey = 'count', // 'count' or 'total' to control chart proportions
    customColors = {},
    percentageInParentheses = false,
}) {
    // Helper function to extract count & total from item
    const parseItemData = (itemData) => {
        if (typeof itemData === 'number') {
            return { count: itemData, total: 0 };
        }
        if (itemData && typeof itemData === 'object') {
            return {
                count: Number(itemData.count ?? itemData.value ?? 0),
                total: Number(itemData.total ?? 0),
            };
        }
        return { count: 0, total: 0 };
    };

    // Map incoming dynamic data and colors into the config structure
    const formattedData = config.map((item) => {
        let parsed = { count: 0, total: 0 };

        if (Array.isArray(data)) {
            const match = data.find((d) => d.id === item.id);
            parsed = parseItemData(match);
        } else if (data && typeof data === 'object') {
            let rawVal = data[item.id];

            // Key fallback for unsettled vs unsettle
            if (rawVal === undefined && item.id === 'unsettled') {
                rawVal = data.unsettle;
            }

            parsed = parseItemData(rawVal);
        }

        // Selected value used for chart rendering calculations
        const chartValue = valueKey === 'total' ? parsed.total : parsed.count;

        return {
            ...item,
            color: customColors[item.id] || item.color,
            count: parsed.count,
            totalAmount: parsed.total,
            value: Number(chartValue) || 0,
        };
    });

    // Calculate totals across all items
    const totalChartValue = formattedData.reduce((acc, item) => acc + item.value, 0);
    const grandTotalCount = formattedData.reduce((acc, item) => acc + item.count, 0);
    const grandTotalAmount = formattedData.reduce((acc, item) => acc + item.totalAmount, 0);

    // Chart.js Data Configuration
    const chartData = {
        labels: formattedData.map((d) => d.label),
        datasets: [
            {
                data: formattedData.map((d) => d.value),
                backgroundColor: formattedData.map((d) => d.color),
                borderWidth: 2,
                borderColor: '#FFFFFF',
                borderRadius: 4,
            },
        ],
    };

    // Chart.js Options
    const chartOptions = {
        cutout: '78%',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (context) {
                        const val = context.raw || 0;
                        const pct = totalChartValue > 0 ? ((val / totalChartValue) * 100).toFixed(1) : '0.0';
                        return ` ${context.label}: ${val.toLocaleString()} (${pct}%)`;
                    },
                },
            },
        },
    };

    return (
        <Card
            sx={{
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardContent
                sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:last-child': { pb: 3 },
                }}
            >
                {/* Header Title & Subtitle */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 3 }}>
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, fontSize: '1.125rem' }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                <Grid container spacing={3} alignItems="center" sx={{ flexGrow: 1 }}>
                    {/* Doughnut Chart with Center Text Overlay */}
                    <Grid item xs={12} sm={5}>
                        <Box
                            sx={{
                                position: 'relative',
                                width: 190,
                                height: 190,
                                mx: 'auto',
                            }}
                        >
                            <Doughnut data={chartData} options={chartOptions} />

                            {/* Center Text */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                                >
                                    {centerLabel}
                                </Typography>

                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 800, lineHeight: 1.1 }}
                                >
                                    {grandTotalCount.toLocaleString()}
                                </Typography>

                                {grandTotalAmount > 0 && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600, display: 'block', mt: 0.5 }}
                                    >
                                        ${grandTotalAmount.toLocaleString()}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right Breakdown / Legend */}
                    <Grid item xs={12} sm={7}>
                        <Stack spacing={2}>
                            {formattedData.map((item) => {
                                const percentage = totalChartValue > 0 
                                    ? ((item.value / totalChartValue) * 100).toFixed(1) 
                                    : '0.0';

                                return (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1
                                        }}
                                    >
                                        {/* Color Dot + Label */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 90 }}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: percentageInParentheses ? '50%' : '3px',
                                                    bgcolor: item.color,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {item.label}
                                            </Typography>
                                        </Box>

                                        {/* Values Breakdown */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'right' }}>
                                            {/* Show count */}
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {item.count.toLocaleString()}
                                            </Typography>

                                            {/* Show total monetary amount if available */}
                                            {item.totalAmount > 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, marginTop: 0 }}>
                                                    ({item.totalAmount.toLocaleString()} Ks)
                                                </Typography>
                                            )}

                                            {/* Percentage */}
                                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, minWidth: 45, marginTop: 0 }}>
                                                {percentageInParentheses ? `(${percentage}%)` : `${percentage}%`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}